import { IFormsTemplate, SHARED_FORM_TEMPLATE_KEYS } from '@commenda/api';

const AFFILIATE_ROUTE_PREFIX = '/affiliate/';

/** Pure rules for the affiliate form builder: which templates are Commenda's shared, read-only
 * ones, and the hrefs that keep every hop inside the affiliate area. */
export class AffiliateFormHelper {
  static isSharedTemplate(template: Pick<IFormsTemplate, 'key'>): boolean {
    return (
      template.key != null && SHARED_FORM_TEMPLATE_KEYS.includes(template.key)
    );
  }

  static getFormsListHref(firmId: string): string {
    return `${AFFILIATE_ROUTE_PREFIX}${firmId}/forms`;
  }

  static getFormVersionsHref(firmId: string, templateName: string): string {
    return `${AffiliateFormHelper.getFormsListHref(firmId)}/versions?name=${encodeURIComponent(templateName)}`;
  }

  static getFormEditorPath(firmId: string): string {
    return `${AffiliateFormHelper.getFormsListHref(firmId)}/editor`;
  }

  static getFormPreviewPath(firmId: string): string {
    return `${AffiliateFormHelper.getFormsListHref(firmId)}/preview`;
  }

  /** `returnHref` rides along as `?from=` so the editor's back button can return to a workflow. */
  static getFormEditorHref(
    firmId: string,
    templateId: string,
    returnHref?: string
  ): string {
    const editorHref = `${AffiliateFormHelper.getFormEditorPath(firmId)}?templateId=${templateId}`;
    return returnHref === undefined
      ? editorHref
      : `${editorHref}&from=${encodeURIComponent(returnHref)}`;
  }

  /** The draft key is namespaced per firm so two firms in one browser cannot restore each
   * other's unsaved form. */
  static getDraftStorageKey(firmId: string): string {
    return `affiliate-form-builder:${firmId}`;
  }

  /** Only a path inside this firm's affiliate area may be returned to: it must start with
   * `/affiliate/<firmId>/`, and a `..` segment or a backslash drops it. */
  static sanitizeReturnHref(
    candidate: string | undefined,
    firmId: string
  ): string | undefined {
    if (candidate === undefined || firmId === '') return undefined;
    const firmPrefix = `${AFFILIATE_ROUTE_PREFIX}${firmId}/`;
    if (!candidate.startsWith(firmPrefix) || candidate.includes('\\')) {
      return undefined;
    }
    const pathname = candidate.replace(/[?#].*$/, '');
    const hasParentSegment = pathname
      .split('/')
      .some((segment) => segment === '..');
    return hasParentSegment ? undefined : candidate;
  }
}
